import * as React from 'react';
import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import uuid from 'uuid/v4';
import moment from 'moment';

import { Alert, Button, Col, ControlLabel, FormGroup, HelpBlock, Row } from 'components/graylog';
import { Input } from 'components/bootstrap';
import { extractDurationAndUnit } from 'components/common/TimeUnitInput';
import { TimeUnitInput, Spinner } from 'components/common';
import { getValueFromInput } from 'util/FormsUtils';
import { isPermitted } from 'util/PermissionsMixin';
import CurrentUserContext from 'contexts/CurrentUserContext';

import CorrelationRules from './CorrelationRules';
import CorrelationPreview from './CorrelationPreview';
import commonStyles from './commonStyles.css';

export const TIME_UNITS = ['HOURS', 'MINUTES', 'SECONDS'];

const getNewRule = () => {
  return { id: uuid(), occurrence: 1, event_creator_equals: '' };
};

type Rule = {
  event_creator_equals: string,
  id: string,
  occurrence: number,
};

type Source = {
  event_definition_id: string,
  title: string,
  event_definition_type: string,
};

type Config = {
  execute_every_ms: number,
  search_within_ms: number,
  rules: Rule[],
  sources: Source[],
  _is_scheduled: boolean,
};

type Validation = {
  errors: {
    search_within_ms: string,
    rules: string,
    execute_every_ms: string,

  },
};

type Props = {
  eventDefinition: {
    config: Config,
  },
  validation: Validation,
  eventDefinitions: {
    id: string,
    title: string,
    config: {
      type: string,
    },
  }[],
  onChange: (string, Config) => void,
};

const CorrelationForm = ({ eventDefinition, validation, eventDefinitions, onChange }: Props) => {
  const { execute_every_ms: executeEveryMs, search_within_ms: searchWithinMs } = eventDefinition.config;
  const searchWithin = extractDurationAndUnit(searchWithinMs, TIME_UNITS);
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);

  const [searchWithinMsDuration, setSearchWithinMsDuration] = useState(searchWithin.duration);
  const [searchWithinMsUnit, setSearchWithinMsUnit] = useState(searchWithin.unit);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState(executeEvery.duration);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState(executeEvery.unit);

  const currentUser = useContext(CurrentUserContext);

  const updateSources = (config, currentEventDefinitionId, nextEventDefinitionId) => {
    const nextConfig = lodash.cloneDeep(config);
    const isEventDefinitionUsed = nextConfig.rules
      .filter((r) => r.event_creator_equals === currentEventDefinitionId).length > 1;

    if (!isEventDefinitionUsed) {
      // Event definition is not used in any rule, delete it from sources
      nextConfig.sources = nextConfig.sources.filter((s) => s.event_definition_id !== currentEventDefinitionId);
    }

    if (nextEventDefinitionId === undefined) {
      return nextConfig;
    }

    const isNextEventDefinitionInSources = nextConfig.sources.find((s) => s.event_definition_id === nextEventDefinitionId);

    if (!isNextEventDefinitionInSources) {
      // Add source with ID to new Event Definition
      const nextEventDefinition = eventDefinitions.find((d) => d.id === nextEventDefinitionId);

      if (!nextEventDefinition) {
        throw new Error(`Could not find event definition with id: ${nextEventDefinitionId}`);
      }

      const source = {
        title: nextEventDefinition.title,
        event_definition_type: nextEventDefinition.config.type,
        event_definition_id: nextEventDefinition.id,
      };
      nextConfig.sources.push(source);
    }

    return nextConfig;
  };

  const handleTimeRangeChange = (key) => {
    return (nextValue, nextUnit) => {
      const config = lodash.cloneDeep(eventDefinition.config);
      config[key] = moment.duration(lodash.max([nextValue, 1]), nextUnit).asMilliseconds();
      onChange('config', config);

      if (key === 'search_within_ms') {
        setSearchWithinMsDuration(nextValue);
        setSearchWithinMsUnit(nextUnit);
      } else {
        setExecuteEveryMsDuration(nextValue);
        setExecuteEveryMsUnit(nextUnit);
      }
    };
  };

  const handleRuleChange = (id, nextRule) => {
    let config = lodash.cloneDeep(eventDefinition.config);
    const rule = config.rules.find((r) => r.id === id);
    const ruleIdx = config.rules.indexOf(rule);

    if (rule && rule.event_creator_equals !== nextRule.event_creator_equals) {
      config = updateSources(config, rule.event_creator_equals, nextRule.event_creator_equals);
    }

    config.rules[ruleIdx] = nextRule;
    onChange('config', config);
  };

  const handleConfigChange = (event) => {
    const { name } = event.target;
    const config = lodash.cloneDeep(eventDefinition.config);
    config[name] = getValueFromInput(event.target);
    onChange('config', config);
  };

  const handleAddEvent = () => {
    const config = lodash.cloneDeep(eventDefinition.config);
    config.rules.push(getNewRule());
    onChange('config', config);
  };

  const handleRemoveEvent = (id) => {
    let config = lodash.cloneDeep(eventDefinition.config);
    const rule = config.rules.find((r) => r.id === id);
    config.rules = lodash.without(config.rules, rule);

    if (rule && rule.event_creator_equals) {
      config = updateSources(config, rule.event_creator_equals, undefined);
    }

    onChange('config', config);
  };

  const missingEventPermissions = () => {
    const rules = eventDefinition?.config?.rules;
    const eventDefinitionIds = rules.map((r) => r.event_creator_equals);

    return eventDefinitionIds.filter(
      (eventId) => !isPermitted(currentUser?.permissions, `eventdefinitions:read:${eventId}`),
    );
  };

  if (!eventDefinition.config || !eventDefinition.config.rules || eventDefinition.config.rules.length === 0) {
    return <Spinner />;
  }

  const missingEventDefinitionPermissions = missingEventPermissions();

  if (missingEventDefinitionPermissions.length > 0) {
    return (
      <Alert bsStyle="danger">Missing Event Definition Permissions for:<br />
        {missingEventDefinitionPermissions.join(', ')}
      </Alert>
    );
  }

  const ruleErrors = lodash.get(validation, 'errors.rules', []);
  const eventCreatorErrors = lodash.get(validation, 'errors.event_creator_equals', []);
  const errors = [...ruleErrors, ...eventCreatorErrors];

  return (
    <Row>
      <Col md={7} lg={6}>
        <h2 className={commonStyles.title}>Event Correlation Rules</h2>
        <p>
          Event Correlation Rules allow you to analyze complex sequences of Events to identify meaningful incidents.
        </p>
        {eventDefinitions.length === 0 ? (
          <Alert bsStyle="info">
            Event Correlation Rules work on top of other Events. Please start creating Filter & Aggregation Event
            Definitions first.
          </Alert>
        ) : (
          <div className={commonStyles.correlationForm}>
            <fieldset>
              <div className={`form-inline ${commonStyles.inlineFormGroup}`}>
                <FormGroup validationState={validation.errors.search_within_ms ? 'error' : null}>
                  <ControlLabel>The following Sequence of Events must be satisfied within</ControlLabel>
                  <TimeUnitInput update={handleTimeRangeChange('search_within_ms')}
                                 value={searchWithinMsDuration}
                                 unit={searchWithinMsUnit}
                                 units={TIME_UNITS}
                                 clearable
                                 required />
                  {validation.errors.search_within_ms && (
                    <HelpBlock>{validation.errors.search_within_ms[0]}</HelpBlock>
                  )}
                </FormGroup>
              </div>

              <div className={`form-inline ${commonStyles.inlineFormGroup}`}>
                <FormGroup validationState={validation.errors.execute_every_ms ? 'error' : null}>
                  <ControlLabel>The correlation should execute every</ControlLabel>
                  <TimeUnitInput update={handleTimeRangeChange('execute_every_ms')}
                                 value={executeEveryMsDuration}
                                 unit={executeEveryMsUnit}
                                 units={TIME_UNITS}
                                 clearable
                                 required />
                  {validation.errors.execute_every_ms && (
                    <HelpBlock>{validation.errors.execute_every_ms[0]}</HelpBlock>
                  )}
                </FormGroup>
              </div>
              <Input id="schedule-checkbox"
                     type="checkbox"
                     name="_is_scheduled"
                     label="Enable"
                     help="Should this event definition be executed automatically?"
                     checked={lodash.defaultTo(eventDefinition.config._is_scheduled, true)}
                     onChange={handleConfigChange} />
            </fieldset>

            {errors.length > 0 && (
              <Alert bsStyle="danger" className={commonStyles.validationSummary}>
                <h4>Correlation rules with errors</h4>
                <p>Please correct the following errors before saving this Event Definition:</p>
                <ul>
                  {errors.map((error) => {
                    return <li key={error}>{error}</li>;
                  })}
                </ul>
              </Alert>
            )}

            <CorrelationRules rules={eventDefinition.config.rules}
                              eventDefinitions={eventDefinitions}
                              onChange={handleRuleChange}
                              onRemove={handleRemoveEvent} />
            <Button bsStyle="success" onClick={handleAddEvent}>Add Event</Button>
          </div>
        )}
      </Col>
      <Col md={5} lg={5} lgOffset={1}>
        {eventDefinitions.length > 0 && (
          <CorrelationPreview rules={eventDefinition.config.rules}
                              eventDefinitions={eventDefinitions} />
        )}
      </Col>
    </Row>
  );
};

CorrelationForm.propTypes = {
  eventDefinition: PropTypes.object.isRequired,
  validation: PropTypes.object.isRequired,
  eventDefinitions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

CorrelationForm.defaultConfig = {
  sources: [],
  rules: [getNewRule()],
  search_within_ms: 60 * 1000,
  execute_every_ms: 60 * 1000,
};

export default CorrelationForm;
